import React from "react";
import { Link } from "react-router-dom";
import { GridLayout } from "components/grid";
import { DataDisplayRecord } from "components/data-display";
import { ShowHide } from "components/layout";
import {
    GreyFleetCheckCoverRecords,
    GreyFleetCheckInsuranceOverview,
    GreyFleetCheckLicenceOverview,
    GreyFleetCheckVehicleOverview,
    hasRequiredCoverTypes,
} from "pages/licence-checking/grey-fleet/check/overview";
import PropTypes from "prop-types";

const defaultArray = [];

export function GreyFleetCheckDetail({
    isLoading,
    checkData,
    evaluateStatus,
    refreshCheck,
    licenceCheckingOptions,
    coverTypes = defaultArray,
}) {
    function renderStatus(value, mapping) {
        const statusClass = mapping[value?.toLowerCase()];
        if (statusClass) {
            if (value.toLowerCase() === "rejected") {
                return (
                    <span className={statusClass}>{`Rejected by ${
                        checkData.enrolled ? "Employee" : "Manager"
                    }`}</span>
                );
            } else {
                return <span className={statusClass}>{value}</span>;
            }
        } else {
            return <span className="text-intent-info">{value}</span>;
        }
    }

    return (
        <div>
            <div className="spacer-bottom">
                <h2>Check Details</h2>

                <GridLayout numberOfColumns={4} gap={30}>
                    <DataDisplayRecord
                        header="Grey Fleet Check Status"
                        data={renderStatus(checkData.status, {
                            valid: "text-intent-success",
                            invalid: "text-intent-danger",
                            rejected: "text-intent-danger",
                        })}
                        loading={isLoading}
                    />
                    <DataDisplayRecord
                        header="Vehicle Registration"
                        data={checkData.registration}
                        loading={isLoading}
                    />
                    <DataDisplayRecord
                        header="Employee Name"
                        data={
                            <Link
                                key={`${checkData.id}-name`}
                                to={`/employee/${checkData.employeeId}/licence-checking`}
                            >
                                {checkData.employeeName}
                            </Link>
                        }
                        loading={isLoading}
                    />
                    <DataDisplayRecord
                        header="Employee Location"
                        data={checkData.location}
                        loading={isLoading}
                    />

                    <DataDisplayRecord
                        header="Employee Email"
                        data={checkData.emailAddress ?? "-"}
                        loading={isLoading}
                    />
                </GridLayout>
            </div>

            <ShowHide
                evaluator={checkData.statusKey !== "REJECTED"}
                show={
                    <GreyFleetCheckInsuranceOverview
                        isLoading={isLoading}
                        checkData={checkData}
                        evaluateStatus={evaluateStatus}
                        reloadCheck={refreshCheck}
                        coverTypes={coverTypes}
                    />
                }
            />

            {/* A check can be rejected because the declared cover fell short of what the company
                required, so the two cover records stay readable after a rejection even though the
                rest of the insurance overview is hidden. */}
            <ShowHide
                evaluator={
                    checkData.statusKey === "REJECTED" &&
                    hasRequiredCoverTypes(checkData?.insurance, coverTypes)
                }
                show={
                    <div className="spacer-bottom">
                        <h2>Insurance Check</h2>
                        <GridLayout numberOfColumns={4} gap={30}>
                            <GreyFleetCheckCoverRecords
                                isLoading={isLoading}
                                insurance={checkData?.insurance}
                                coverTypes={coverTypes}
                            />
                        </GridLayout>
                    </div>
                }
            />

            <ShowHide
                evaluator={checkData.statusKey !== "REJECTED"}
                show={
                    <GreyFleetCheckVehicleOverview
                        isLoading={isLoading}
                        checkData={checkData}
                        evaluateStatus={evaluateStatus}
                        refreshCheck={refreshCheck}
                        licenceCheckingOptions={licenceCheckingOptions}
                        coverTypes={coverTypes}
                    />
                }
            />

            <ShowHide
                evaluator={checkData.statusKey !== "REJECTED"}
                show={
                    <GreyFleetCheckLicenceOverview
                        isLoading={isLoading}
                        checkData={checkData}
                        evaluateStatus={evaluateStatus}
                        reloadCheck={refreshCheck}
                    />
                }
            />
        </div>
    );
}

GreyFleetCheckDetail.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    checkData: PropTypes.object.isRequired,
    evaluateStatus: PropTypes.func.isRequired,
    refreshCheck: PropTypes.func.isRequired,
    licenceCheckingOptions: PropTypes.object.isRequired,
    coverTypes: PropTypes.array,
};
